import { api } from "@/lib/apiClient";
import { isAxiosError } from "axios";

export type AttendanceAdjustmentType = "add" | "deduct";

export type AttendanceAdjustmentRow = {
  id: string;
  employeeId: string;
  employeeName: string | null;
  adjustmentDate: string;
  adjustmentType: AttendanceAdjustmentType;
  minutes: number | null;
  startOtMinutes: number | null;
  endOtMinutes: number | null;
  reason: string | null;
  createdBy: string | null;
  createdAt: string;
  updatedAt: string;
};

export type AttendanceAdjustmentEffective = {
  minutes: number;
  startOtMinutes: number;
  endOtMinutes: number;
};

function str(v: unknown): string {
  if (v == null || v === "") return "";
  return String(v);
}

function mapType(raw: unknown): AttendanceAdjustmentType {
  return str(raw) === "deduct" ? "deduct" : "add";
}

function mapNullableMinutes(raw: unknown): number | null {
  if (raw == null || raw === "") return null;
  const n = typeof raw === "number" ? raw : Number(raw);
  if (!Number.isFinite(n)) return null;
  return Math.max(0, Math.round(n));
}

function mapRow(raw: Record<string, unknown>): AttendanceAdjustmentRow {
  return {
    id: str(raw.id),
    employeeId: str(raw.employee_id),
    employeeName: raw.employee_name != null ? str(raw.employee_name) : null,
    adjustmentDate: str(raw.adjustment_date).slice(0, 10),
    adjustmentType: mapType(raw.adjustment_type),
    minutes: mapNullableMinutes(raw.minutes),
    startOtMinutes: mapNullableMinutes(raw.start_ot_minutes),
    endOtMinutes: mapNullableMinutes(raw.end_ot_minutes),
    reason: raw.reason != null ? str(raw.reason) : null,
    createdBy: raw.created_by != null ? str(raw.created_by) : null,
    createdAt: str(raw.created_at),
    updatedAt: str(raw.updated_at),
  };
}

function mapEffective(raw: Record<string, unknown> | undefined): AttendanceAdjustmentEffective {
  return {
    minutes: Math.round(Number(raw?.minutes) || 0),
    startOtMinutes: Math.round(Number(raw?.start_ot_minutes) || 0),
    endOtMinutes: Math.round(Number(raw?.end_ot_minutes) || 0),
  };
}

/** Format stored minutes as e.g. "1h 30m" or "45m". */
export function formatAdjustmentMinutes(totalMinutes: number): string {
  if (!Number.isFinite(totalMinutes) || totalMinutes <= 0) return "0m";
  const h = Math.floor(totalMinutes / 60);
  const m = totalMinutes % 60;
  if (h > 0 && m > 0) return `${h}h ${m}m`;
  if (h > 0) return `${h}h`;
  return `${m}m`;
}

export function formatSplitOtAdjustment(row: AttendanceAdjustmentRow): string {
  const parts: string[] = [];
  if ((row.startOtMinutes ?? 0) > 0) {
    parts.push(`Start OT ${formatAdjustmentMinutes(row.startOtMinutes ?? 0)}`);
  }
  if ((row.endOtMinutes ?? 0) > 0) {
    parts.push(`End OT ${formatAdjustmentMinutes(row.endOtMinutes ?? 0)}`);
  }
  if (parts.length === 0) {
    return formatAdjustmentMinutes(row.minutes ?? 0);
  }
  return parts.join(" · ");
}

export function formatSignedAdjustmentMinutes(totalMinutes: number): string {
  if (!Number.isFinite(totalMinutes) || totalMinutes === 0) return "0m";
  const sign = totalMinutes < 0 ? "−" : "+";
  return `${sign}${formatAdjustmentMinutes(Math.abs(totalMinutes))}`;
}

export function attendanceAdjustmentsApiErrorMessage(error: unknown): string {
  if (isAxiosError(error)) {
    const data = error.response?.data as { message?: string; errors?: Record<string, string[]> } | undefined;
    if (data?.errors) {
      const first = Object.values(data.errors).flat()[0];
      if (first) return first;
    }
    if (typeof data?.message === "string") return data.message;
  }
  if (error instanceof Error && error.message) return error.message;
  return "Something went wrong.";
}

export async function listAttendanceAdjustments(params: {
  employeeId: string;
  adjustmentDate: string;
}): Promise<{ rows: AttendanceAdjustmentRow[]; effective: AttendanceAdjustmentEffective }> {
  const response = await api.get<{
    data?: Record<string, unknown>[];
    meta?: { effective?: Record<string, unknown> };
  }>("/api/v1/attendance/adjustments", {
    params: {
      employee_id: params.employeeId,
      adjustment_date: params.adjustmentDate,
    },
  });
  const list = Array.isArray(response.data?.data) ? response.data.data : [];
  return {
    rows: list.map((r) => mapRow(r as Record<string, unknown>)),
    effective: mapEffective(response.data?.meta?.effective),
  };
}

export async function createAttendanceAdjustments(payload: {
  employeeId: string;
  adjustmentDate: string;
  lines: Array<{
    adjustmentType?: AttendanceAdjustmentType;
    minutes?: number;
    startOtMinutes?: number | null;
    endOtMinutes?: number | null;
    startOtAdjustmentType?: AttendanceAdjustmentType;
    endOtAdjustmentType?: AttendanceAdjustmentType;
    reason?: string | null;
  }>;
}): Promise<{ rows: AttendanceAdjustmentRow[]; message: string; replaced: boolean }> {
  const response = await api.post<{
    message?: string;
    data?: Record<string, unknown>[];
    meta?: { replaced?: boolean };
  }>(
    `/api/v1/employees/${encodeURIComponent(payload.employeeId)}/attendance-adjustments`,
    {
      adjustment_date: payload.adjustmentDate,
      lines: payload.lines.map((line) => {
        const isSplit =
          line.startOtMinutes != null ||
          line.endOtMinutes != null ||
          line.startOtAdjustmentType != null ||
          line.endOtAdjustmentType != null;

        if (isSplit) {
          const body: Record<string, unknown> = {
            start_ot_minutes: line.startOtMinutes ?? 0,
            end_ot_minutes: line.endOtMinutes ?? 0,
            start_ot_adjustment_type: line.startOtAdjustmentType ?? line.adjustmentType ?? "add",
            end_ot_adjustment_type: line.endOtAdjustmentType ?? line.adjustmentType ?? "add",
            reason: line.reason ?? null,
          };
          if (line.adjustmentType) {
            body.adjustment_type = line.adjustmentType;
          }
          return body;
        }

        return {
          adjustment_type: line.adjustmentType ?? "add",
          minutes: line.minutes ?? 0,
          reason: line.reason ?? null,
        };
      }),
    },
  );
  const list = Array.isArray(response.data?.data) ? response.data.data : [];
  return {
    rows: list.map((r) => mapRow(r as Record<string, unknown>)),
    message: str(response.data?.message) || "Attendance adjustment recorded.",
    replaced: Boolean(response.data?.meta?.replaced),
  };
}

export async function deleteAttendanceAdjustment(id: string): Promise<{ message: string }> {
  const response = await api.delete<{ message?: string }>(
    `/api/v1/attendance/adjustments/${encodeURIComponent(id)}`,
  );
  return { message: str(response.data?.message) || "Attendance adjustment deleted." };
}
